import { SystemPrototypeTemplateEntity } from '@agilelab/plugin-wb-builder-common';
import CloseIcon from '@material-ui/icons/Close';
import {
  ConfirmDialog,
  customAlertApiRef,
  WbDrawer,
  WbMetadataTable,
} from '@agilelab/plugin-wb-platform';
import {
  MultiStepJsonEditForm,
  useFormSteps,
} from '@agilelab/plugin-wb-scaffolder';
import {
  DEFAULT_NAMESPACE,
  parseEntityRef,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import {
  IconButton,
  LinearProgress,
  makeStyles,
  Paper,
} from '@material-ui/core';
import { useApi } from '@backstage/core-plugin-api';
import { LayoutOptions } from '@agilelab/plugin-wb-scaffolder';
import { Rating } from '@material-ui/lab';
import { SystemPrototype } from '@agilelab/plugin-wb-system-prototype-common';
import { useEffect, useMemo, useRef, useState } from 'react';
import { IChangeEvent } from '@rjsf/core';

interface SystemPrototypeParameterSection {
  title: string;
  required?: string[];
  properties?: Record<string, any>;
  [key: string]: any;
}

function getSectionedReviewData(
  formData: any,
  template: SystemPrototypeTemplateEntity | undefined,
) {
  if (!template?.spec?.parameters) {
    return [];
  }

  const result = (
    template.spec.parameters as unknown as SystemPrototypeParameterSection[]
  ).map(section => {
    const sectionFields: Array<{
      key: string;
      title: string;
      value: any;
      type?: string;
    }> = [];
    const properties = section.properties || {};

    Object.entries(properties).forEach(([key, prop]) => {
      const paramName = `${section.title}.${key}`;
      const param = formData?.parameters?.find(
        (p: any) => p.name === paramName,
      );

      sectionFields.push({
        key,
        title: prop.title || key,
        value: param?.default ?? '',
        type: key === 'importance' ? 'stars' : undefined,
      });
    });

    return {
      title: section.title,
      fields: sectionFields,
    };
  });

  return result;
}

const useStyles = makeStyles(() => ({
  scrollable: {
    flex: '1 0 auto',
    minHeight: 0,
    overflow: 'auto',
  },
}));

interface EditSystemPrototypeSidebarProps {
  prototype: SystemPrototype;
  template?: SystemPrototypeTemplateEntity;
  onClose: () => void;
  onSave: (data: Partial<SystemPrototype>) => void | Promise<void>;
  editedSection: string | null;
  initialSectionIndex?: number;
  layouts?: LayoutOptions[];
}

export const EditSystemPrototypeSidebar = ({
  onClose,
  editedSection,
  template,
  prototype,
  onSave,
  layouts,
}: EditSystemPrototypeSidebarProps) => {
  const classes = useStyles();
  const alertApi = useApi(customAlertApiRef);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const normalizedFormData = useMemo(() => {
    if (prototype && template) {
      let normalizedOwner;
      if (prototype.owner) {
        try {
          parseEntityRef(prototype.owner);
          normalizedOwner = prototype.owner;
        } catch {
          normalizedOwner = normalizeEntityRef('owner', prototype.owner);
        }
      }

      const normalized = {
        ...prototype,
        parameters: (() => {
          const paramArray: Array<{
            name: string;
            section: string;
            type: 'string' | 'number' | 'boolean' | 'object' | 'array';
            description?: string;
            default?: string | number | boolean | object | unknown[];
            required?: boolean;
            enum?: (string | number | boolean | object)[];
          }> = [];
          if (template.spec?.parameters) {
            (
              template.spec
                .parameters as unknown as SystemPrototypeParameterSection[]
            ).forEach(section => {
              const properties = section.properties || {};
              Object.entries(properties).forEach(([key, prop]) => {
                const params = prototype.parameters as unknown as Record<
                  string,
                  Record<string, any>
                >;
                const sectionKey = `${section.title}.${key}`;
                paramArray.push({
                  name: sectionKey,
                  section: section.title,
                  type: prop.type || 'string',
                  description: prop.description,
                  default:
                    params?.[section.title]?.[key] ?? (prototype as any)[key],
                  required: prop.required,
                  enum: prop.enum,
                });
              });
            });
          }
          return paramArray;
        })(),
        systemPrototypeType: prototype.systemPrototypeType,
        ...(prototype.owner && { owner: normalizedOwner }),
        ...(prototype.domain && {
          domain: normalizeEntityRef('domain', prototype.domain),
        }),
        dataSources: (prototype as any).dataSources || [],
        businessConcepts: (prototype as any).businessConcepts || [],
      };
      return normalized;
    }
    return undefined;
  }, [prototype, template]);

  const [formData, setFormData] = useState<
    Partial<SystemPrototype> | undefined
  >(normalizedFormData);

  const memoizedParameters = useMemo(() => {
    if (!formData?.parameters) return {};

    const params = (
      Array.isArray(formData.parameters)
        ? formData.parameters.filter(Boolean)
        : Object.values(formData.parameters).filter(Boolean)
    ) as Array<{
      name: string;
      section: string;
      default?: any;
    }>;

    const result = params.reduce<Record<string, any>>((acc, param) => {
      const [section, key] = param.name.split('.');
      const templateParams = template?.spec?.parameters as
        | SystemPrototypeParameterSection[]
        | undefined;
      const sectionConfig = templateParams?.find(p => p.title === section);
      const propertyConfig = sectionConfig?.properties?.[key] as
        | Record<string, any>
        | undefined;

      if (propertyConfig?.['ui:field']) {
        const fieldType = propertyConfig['ui:field'];

        if (fieldType === 'OwnerPicker') {
          const ownerValue =
            (formData as Record<string, any>)[key] || formData.owner;
          return {
            ...acc,
            [param.name]: ownerValue,
          };
        }

        if (fieldType === 'EntityPicker') {
          const entityValue = (formData as Record<string, any>)[key];
          if (entityValue) {
            if (entityValue.includes(':')) {
              return {
                ...acc,
                [param.name]: entityValue,
              };
            }
            try {
              const normalizedValue = normalizeEntityRef(key, entityValue);
              return {
                ...acc,
                [param.name]: normalizedValue,
              };
            } catch (e) {
              return {
                ...acc,
                [param.name]: entityValue,
              };
            }
          }
        }
      }

      return {
        ...acc,
        [param.name]: param.default,
      };
    }, {});
    return result;
  }, [formData, template?.spec?.parameters]);

  const didSave = useRef(false);

  useEffect(() => {
    setFormData(normalizedFormData);
  }, [normalizedFormData]);

  const wrappedOnClose = () => {
    if (!didSave.current) {
      setShowConfirmDialog(true);
    } else {
      didSave.current = false;
      onClose();
    }
  };

  const handleConfirmClose = () => {
    setFormData(normalizedFormData);
    didSave.current = false;
    setShowConfirmDialog(false);
    onClose();
  };

  const handleCancelClose = (_event: React.UIEvent, _reason: string) => {
    setShowConfirmDialog(false);
  };

  const handleComplete = async () => {
    let updatedData = {
      ...formData,
    };

    if (updatedData.parameters) {
      const sectionedParameters: Record<string, Record<string, any>> = {};

      (
        updatedData.parameters as Array<{
          name: string;
          section: string;
          type: 'string' | 'number' | 'boolean' | 'object' | 'array';
          description?: string;
          default?: any;
          required?: boolean;
          enum?: (string | number | boolean | object)[];
        }>
      ).forEach(param => {
        const [section, key] = param.name.split('.');
        if (!sectionedParameters[section]) {
          sectionedParameters[section] = {};
        }
        sectionedParameters[section][key] = param.default;
      });

      updatedData = {
        ...updatedData,
        parameters: sectionedParameters as any,
        ...(updatedData.owner && {
          owner: normalizeEntityRef('owner', updatedData.owner),
        }),
        ...(updatedData.domain && {
          domain: normalizeEntityRef('domain', updatedData.domain),
        }),
      };
    }

    try {
      didSave.current = true;
      await onSave(updatedData);
      wrappedOnClose();
    } catch (err) {
      didSave.current = false;

      const errorMessage =
        err instanceof Error
          ? `Failed to save system prototype: ${err.message}`
          : 'Failed to save system prototype';

      alertApi.post({
        message: errorMessage,
        severity: 'error',
        timeoutMillis: 5000,
      });
    }
  };

  const steps = useMemo(() => {
    if (!template?.spec?.parameters) {
      return [];
    }

    const computedSteps = (
      template.spec.parameters as unknown as SystemPrototypeParameterSection[]
    ).map(param => {
      const properties = param.properties || {};
      const schema = {
        type: 'object',
        properties: Object.entries(properties).reduce((acc, [key, prop]) => {
          return {
            ...acc,
            [`${param.title}.${key}`]: prop,
          };
        }, {}),
        required: (param.required || []).map(key => `${param.title}.${key}`),
      };

      return {
        title: param.title,
        schema,
      };
    });

    return computedSteps;
  }, [template]);

  const { steps: processedSteps, loading } = useFormSteps(steps, { layouts });

  function normalizeEntityRef(field: string, value: string): string {
    if (!value) return value;

    try {
      parseEntityRef(value);
      return value;
    } catch {
      // Ignore parsing error and continue with normalization
    }

    let kind;
    if (field === 'owner') {
      kind = value.includes('@') || value.includes('.') ? 'user' : 'group';
    } else {
      kind = field;
    }
    const name = value.includes('@')
      ? value.split('@')[0]
      : value.toLowerCase();

    return stringifyEntityRef({
      kind,
      namespace: DEFAULT_NAMESPACE,
      name,
    });
  }

  const initialStep = useMemo(() => {
    if (!editedSection) return undefined;

    for (let i = 0; i < steps.length; i++) {
      if (steps[i].title === editedSection) return i;
    }

    return undefined;
  }, [steps, editedSection]);

  return (
    <>
      <WbDrawer
        open={editedSection !== null}
        setOpen={() => {
          wrappedOnClose();
        }}
        onClose={wrappedOnClose}
        title="Editor Wizard"
        hideHeader
        size={90}
      >
        <div className={classes.scrollable}>
          {loading && <LinearProgress data-testid="loading-progress" />}
          {template && !loading && formData && processedSteps && (
            <MultiStepJsonEditForm
              headerActions={
                <IconButton
                  aria-label="clear"
                  onClick={() => {
                    wrappedOnClose();
                  }}
                  size="small"
                >
                  <CloseIcon />
                </IconButton>
              }
              initialStep={initialStep}
              formData={memoizedParameters}
              previewStep={steps.length}
              onChange={(e: IChangeEvent) => {
                const form = e.formData as Record<string, string>;
                const newParameters = Object.entries(form).map(
                  ([name, value]) => {
                    const [section] = name.split('.');
                    const existingParam = formData?.parameters?.find(
                      p => p.name === name,
                    );
                    return {
                      name,
                      section,
                      type: existingParam?.type || 'string',
                      description: existingParam?.description,
                      default: value,
                      required: existingParam?.required,
                      enum: existingParam?.enum,
                    };
                  },
                );

                const updatedData = {
                  ...formData,
                  ...form,
                  parameters: newParameters,
                  name: form?.['Value Proposition.name'] || formData?.name,
                  description:
                    form?.['Value Proposition.description'] ||
                    formData?.description,
                  purpose:
                    form?.['Value Proposition.purpose'] || formData?.purpose,
                  domain:
                    form?.['Value Proposition.domain'] || formData?.domain,
                  owner: form?.['Value Proposition.owner'] || formData?.owner,
                  systemPrototypeType:
                    form?.['Value Proposition.systemPrototypeType'] ||
                    formData?.systemPrototypeType,
                  status:
                    form?.['Value Proposition.status'] || formData?.status,
                  dataSources: form?.dataSources || [],
                  businessConcepts: (formData as any)?.businessConcepts || [],
                } as Partial<SystemPrototype>;

                setFormData(updatedData);
              }}
              steps={processedSteps}
              additionalSteps={[
                {
                  title: 'Review and Save',
                  nextButtonText: 'Save',
                  onSaveButtonClick: handleComplete,
                  disableStepButtons: false,
                  component: (
                    <article>
                      {(() => {
                        const reviewData = getSectionedReviewData(
                          formData,
                          template,
                        );
                        return reviewData.map(section => (
                          <Paper
                            key={section.title}
                            style={{ marginBottom: 32, padding: 24 }}
                          >
                            <h3>{section.title}</h3>
                            <WbMetadataTable
                              metadata={Object.fromEntries(
                                section.fields.map(f => [
                                  f.title,
                                  f.type === 'stars' ? (
                                    <Rating value={Number(f.value)} readOnly />
                                  ) : (
                                    f.value
                                  ),
                                ]),
                              )}
                            />
                          </Paper>
                        ));
                      })()}
                    </article>
                  ),
                },
              ]}
              viewStepperLabel
            />
          )}
        </div>
      </WbDrawer>
      <ConfirmDialog
        open={showConfirmDialog}
        onClose={handleCancelClose}
        onConfirm={handleConfirmClose}
        title="Confirm Close"
        description="Are you sure you want to close the editor? Any unsaved changes will be lost."
        confirmButtonText="Close"
      />
    </>
  );
};
